import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Switch,
  TextField,
  Button,
  Alert,
  Stack,
  Chip,
  CircularProgress,
  Grid,
} from "@mui/material";

import { getAdminSettings, updateAdminSettings } from "../../api/adminApi";

// Helper: map API array -> object for easy access
function toMap(items = []) {
  const out = {};
  for (const it of items) out[it.key] = it;
  return out;
}

// Helper: safe read value from map (returns default if missing)
function readValue(map, key, fallback) {
  const v = map[key]?.value;
  return v === undefined || v === null ? fallback : v;
}

// Compact timestamp like: 2/1 11:58 PM
function compactUpdatedAt(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleString([], {
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return String(ts);
  }
}

// Cleaner row layout than FormControlLabel
function SettingRow({ checked, onChange, title, description, updatedAt }) {
  return (
    <Box sx={{ py: 1.25 }}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <Switch checked={checked} onChange={onChange} />
        </Grid>

        <Grid item xs>
          <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
          {description ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {description}
            </Typography>
          ) : null}
        </Grid>

        <Grid item>
          {updatedAt ? (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Updated {updatedAt}
            </Typography>
          ) : null}
        </Grid>
      </Grid>

      <Divider sx={{ mt: 1.25 }} />
    </Box>
  );
}

export default function AdminSettings() {
  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Original settings from server (for dirty compare)
  const [originalMap, setOriginalMap] = useState({});

  // Editable form state (what user changes)
  const [form, setForm] = useState({
    maintenance_mode: false,
    allow_customer_registration: true,
    allow_courier_registration: true,
    require_pod_for_delivery: true,
    pod_max_files: 5,
    pod_max_file_mb: 10,
    feature_negotiation: true,
    feature_ai_pricing: true,
    platform_commission_pct: 15,
  });

  // Load settings from backend
  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        setSuccess("");

        // Fetch ALL categories (you have general + features + pricing)
        const res = await getAdminSettings();
        const items = res.data?.items || [];
        const map = toMap(items);

        if (!mounted) return;

        setOriginalMap(map);

        // Hydrate form from DB (fallback to defaults if missing)
        setForm({
          maintenance_mode: !!readValue(map, "maintenance_mode", false),
          allow_customer_registration: !!readValue(map, "allow_customer_registration", true),
          allow_courier_registration: !!readValue(map, "allow_courier_registration", true),
          require_pod_for_delivery: !!readValue(map, "require_pod_for_delivery", true),
          pod_max_files: Number(readValue(map, "pod_max_files", 5)),
          pod_max_file_mb: Number(readValue(map, "pod_max_file_mb", 10)),
          feature_negotiation: !!readValue(map, "feature_negotiation", true),
          feature_ai_pricing: !!readValue(map, "feature_ai_pricing", true),
          platform_commission_pct: Number(readValue(map, "platform_commission_pct", 15)),
        });
      } catch (e) {
        console.error(e);
        if (!mounted) return;
        setError("Failed to load settings. Check backend logs and admin auth token.");
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  // Determine what changed vs original
  const updates = useMemo(() => {
    const changed = [];

    const pushIfChanged = (key, newValue) => {
      const old = originalMap[key]?.value;
      const oldStr = JSON.stringify(old);
      const newStr = JSON.stringify(newValue);
      if (oldStr !== newStr) changed.push({ key, value: newValue });
    };

    pushIfChanged("maintenance_mode", form.maintenance_mode);
    pushIfChanged("allow_customer_registration", form.allow_customer_registration);
    pushIfChanged("allow_courier_registration", form.allow_courier_registration);
    pushIfChanged("require_pod_for_delivery", form.require_pod_for_delivery);
    pushIfChanged("pod_max_files", Number(form.pod_max_files));
    pushIfChanged("pod_max_file_mb", Number(form.pod_max_file_mb));
    pushIfChanged("feature_negotiation", form.feature_negotiation);
    pushIfChanged("feature_ai_pricing", form.feature_ai_pricing);
    pushIfChanged("platform_commission_pct", Number(form.platform_commission_pct));

    return changed;
  }, [form, originalMap]);

  const dirty = updates.length > 0;

  function updatedAtFor(key) {
    return compactUpdatedAt(originalMap[key]?.updated_at);
  }

  // Basic validation for numbers
  const podFilesInvalid =
    !Number.isFinite(Number(form.pod_max_files)) ||
    Number(form.pod_max_files) < 1 ||
    Number(form.pod_max_files) > 20;

  const podMbInvalid =
    !Number.isFinite(Number(form.pod_max_file_mb)) ||
    Number(form.pod_max_file_mb) < 1 ||
    Number(form.pod_max_file_mb) > 50;

  const commissionInvalid =
    !Number.isFinite(Number(form.platform_commission_pct)) ||
    Number(form.platform_commission_pct) < 0 ||
    Number(form.platform_commission_pct) > 100;

  const hasInvalid = podFilesInvalid || podMbInvalid || commissionInvalid;

  async function onSave() {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      if (!dirty) {
        setSuccess("No changes to save.");
        return;
      }

      if (hasInvalid) {
        setError("Please fix invalid numeric values before saving.");
        return;
      }

      // Add note to audit trail
      const updatesWithNote = updates.map((u) => ({
        ...u,
        note: "Updated from Admin Settings UI",
      }));

      await updateAdminSettings(updatesWithNote);

      // Reload settings after save to refresh updated_at / updated_by
      const res = await getAdminSettings();
      const items = res.data?.items || [];
      const map = toMap(items);
      setOriginalMap(map);

      setSuccess("Settings saved successfully.");
    } catch (e) {
      console.error(e);
      setError("Failed to save settings. Check backend logs (POST /api/admin/settings).");
    } finally {
      setSaving(false);
    }
  }

  function onReset() {
    setError("");
    setSuccess("");

    // Reset to originalMap values
    const map = originalMap;

    setForm({
      maintenance_mode: !!readValue(map, "maintenance_mode", false),
      allow_customer_registration: !!readValue(map, "allow_customer_registration", true),
      allow_courier_registration: !!readValue(map, "allow_courier_registration", true),
      require_pod_for_delivery: !!readValue(map, "require_pod_for_delivery", true),
      pod_max_files: Number(readValue(map, "pod_max_files", 5)),
      pod_max_file_mb: Number(readValue(map, "pod_max_file_mb", 10)),
      feature_negotiation: !!readValue(map, "feature_negotiation", true),
      feature_ai_pricing: !!readValue(map, "feature_ai_pricing", true),
      platform_commission_pct: Number(readValue(map, "platform_commission_pct", 15)),
    });
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Settings
      </Typography>
      <Typography sx={{ mb: 2, color: "text.secondary" }}>
        General platform controls for launch and long-term operations. Pricing/Promo rules can expand later.
      </Typography>

      {loading ? (
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2 }}>
          <CircularProgress size={22} />
          <Typography>Loading settings…</Typography>
        </Stack>
      ) : (
        <>
          {!!error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {!!success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* STICKY ACTION BAR */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              position: "sticky",
              top: 0,
              zIndex: 5,
              backgroundColor: "background.paper",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography sx={{ fontWeight: 700 }}>General Settings</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {dirty ? `${updates.length} change(s) pending` : "No pending changes"}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                {dirty ? (
                  <Chip label="Unsaved changes" color="warning" />
                ) : (
                  <Chip label="Saved" color="success" variant="outlined" />
                )}

                <Button variant="outlined" onClick={onReset} disabled={!dirty || saving}>
                  Reset
                </Button>

                <Button
                  variant="contained"
                  onClick={onSave}
                  disabled={!dirty || saving || hasInvalid}
                >
                  {saving ? "Saving..." : "Save changes"}
                </Button>
              </Stack>
            </Stack>

            {hasInvalid && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Please fix invalid numeric values before saving.
              </Alert>
            )}
          </Paper>

          {/* PLATFORM */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Platform</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Control availability and signups without redeploying.
            </Typography>
            <Divider sx={{ mb: 1 }} />

            <SettingRow
              checked={form.maintenance_mode}
              onChange={(e) => setForm((p) => ({ ...p, maintenance_mode: e.target.checked }))}
              title="Maintenance mode"
              description="When ON, non-admin API calls return 503. Admin still works."
              updatedAt={updatedAtFor("maintenance_mode")}
            />

            <SettingRow
              checked={form.allow_customer_registration}
              onChange={(e) =>
                setForm((p) => ({ ...p, allow_customer_registration: e.target.checked }))
              }
              title="Allow customer registration"
              description="Turn OFF to pause customer signups."
              updatedAt={updatedAtFor("allow_customer_registration")}
            />

            <SettingRow
              checked={form.allow_courier_registration}
              onChange={(e) =>
                setForm((p) => ({ ...p, allow_courier_registration: e.target.checked }))
              }
              title="Allow courier registration"
              description="Turn OFF to control onboarding and verification backlog."
              updatedAt={updatedAtFor("allow_courier_registration")}
            />
          </Paper>

          {/* DELIVERY RULES */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Delivery rules</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Protect lifecycle integrity (especially important long-term).
            </Typography>
            <Divider sx={{ mb: 1 }} />

            <SettingRow
              checked={form.require_pod_for_delivery}
              onChange={(e) =>
                setForm((p) => ({ ...p, require_pod_for_delivery: e.target.checked }))
              }
              title="Require Proof of Delivery before marking Delivered"
              description="Prevents fake completions and keeps status timeline trustworthy."
              updatedAt={updatedAtFor("require_pod_for_delivery")}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }}>
              <TextField
                label="POD max files"
                type="number"
                value={form.pod_max_files}
                onChange={(e) => setForm((p) => ({ ...p, pod_max_files: e.target.value }))}
                error={podFilesInvalid}
                helperText={
                  podFilesInvalid
                    ? "Enter a number between 1 and 20"
                    : "Max number of photos uploaded for POD"
                }
                inputProps={{ min: 1, max: 20 }}
                fullWidth
              />

              <TextField
                label="POD max file size (MB)"
                type="number"
                value={form.pod_max_file_mb}
                onChange={(e) => setForm((p) => ({ ...p, pod_max_file_mb: e.target.value }))}
                error={podMbInvalid}
                helperText={
                  podMbInvalid ? "Enter a number between 1 and 50" : "Max size per photo"
                }
                inputProps={{ min: 1, max: 50 }}
                fullWidth
              />
            </Stack>
          </Paper>

          {/* FEATURES */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Feature flags</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Enable/disable major flows without code changes.
            </Typography>
            <Divider sx={{ mb: 1 }} />

            <SettingRow
              checked={form.feature_negotiation}
              onChange={(e) => setForm((p) => ({ ...p, feature_negotiation: e.target.checked }))}
              title="Enable negotiation"
              description="Turn OFF to force standard pricing only."
              updatedAt={updatedAtFor("feature_negotiation")}
            />

            <SettingRow
              checked={form.feature_ai_pricing}
              onChange={(e) => setForm((p) => ({ ...p, feature_ai_pricing: e.target.checked }))}
              title="Enable AI pricing endpoints"
              description="Turn OFF to disable AI-related routes temporarily."
              updatedAt={updatedAtFor("feature_ai_pricing")}
            />
          </Paper>

          {/* PRICING DEFAULTS */}
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Pricing defaults</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Simple defaults now; later you can move to structured PricingRule tables.
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <TextField
              label="Platform commission (%)"
              type="number"
              value={form.platform_commission_pct}
              onChange={(e) =>
                setForm((p) => ({ ...p, platform_commission_pct: e.target.value }))
              }
              error={commissionInvalid}
              helperText={
                commissionInvalid
                  ? "Enter a number between 0 and 100"
                  : "Used later when payouts/fees are automated"
              }
              inputProps={{ min: 0, max: 100 }}
              fullWidth
            />
          </Paper>

          {/* Future sections placeholders */}
          <Box sx={{ mt: 3, color: "text.secondary" }}>
            <Typography variant="body2">
              Pricing Rules (later): move pricing logic into structured tables like PricingRule (with
              effective dates, rollback).
            </Typography>
            <Typography variant="body2">
              Promo Rules (later): build PromoRule table (code, discount type, time window, max uses).
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}
